import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  LAYOUT_TYPES,
  LAYOUT_MODE_TYPES,
  LAYOUT_SIDEBAR_TYPES,
  LAYOUT_WIDTH_TYPES,
  LAYOUT_POSITION_TYPES,
  LAYOUT_TOPBAR_THEME_TYPES,
  LEFT_SIDEBAR_SIZE_TYPES,
  LEFT_SIDEBAR_VIEW_TYPES,
  LEFT_SIDEBAR_IMAGE_TYPES,
  PERLOADER_TYPES,
  SIDEBAR_VISIBILITY_TYPES,
} from "../Components/constants/layout";

const LAYOUT_STORAGE_KEY = "layout-storage";

const changeHTMLAttribute = (attribute: string, value: string) => {
  if (typeof document === "undefined") return;
  if (document.documentElement) {
    document.documentElement.setAttribute(attribute, value);
  }
  if (document.body) {
    if (attribute === "data-bs-theme" || attribute === "data-sidebar") {
      document.body.setAttribute(attribute, value);
    }
  }
};

interface LayoutState {
  layoutType: string;
  layoutModeType: string;
  leftSidebarType: string;
  layoutWidthType: string;
  layoutPositionType: string;
  topbarThemeType: string;
  leftsidbarSizeType: string;
  leftSidebarViewType: string;
  leftSidebarImageType: string;
  preloader: string;
  sidebarVisibilitytype: string;
  changeLayout: (layout: any) => void;
  changeLayoutMode: (layoutMode: any) => void;
  changeSidebarTheme: (theme: any) => void;
  changeLayoutWidth: (layoutWidth: any) => void;
  changeLayoutPosition: (layoutposition: any) => void;
  changeTopbarTheme: (topbarTheme: any) => void;
  changeLeftsidebarSizeType: (leftsidebarSizetype: any) => void;
  changeLeftsidebarViewType: (leftsidebarViewtype: any) => void;
  changeSidebarImageType: (leftsidebarImagetype: any) => void;
  changePreLoader: (preloaderTypes: any) => void;
  changeSidebarVisibility: (sidebarVisibilitytype: any) => void;
}

const layoutStoreImpl = (set: any, get: any): LayoutState => ({
  layoutType: LAYOUT_TYPES.VERTICAL,
  leftSidebarType: LAYOUT_SIDEBAR_TYPES.LIGHT,
  layoutModeType: LAYOUT_MODE_TYPES.LIGHTMODE,
  layoutWidthType: LAYOUT_WIDTH_TYPES.FLUID,
  layoutPositionType: LAYOUT_POSITION_TYPES.FIXED,
  topbarThemeType: LAYOUT_TOPBAR_THEME_TYPES.LIGHT,
  leftsidbarSizeType: LEFT_SIDEBAR_SIZE_TYPES.DEFAULT,
  leftSidebarViewType: LEFT_SIDEBAR_VIEW_TYPES.DEFAULT,
  leftSidebarImageType: LEFT_SIDEBAR_IMAGE_TYPES.NONE,
  preloader: PERLOADER_TYPES.DISABLE,
  sidebarVisibilitytype: SIDEBAR_VISIBILITY_TYPES.SHOW,

  changeLayout: (layout: any) => {
    if (layout === "twocolumn") document.documentElement.removeAttribute("data-layout-width");
    else if (layout === "horizontal") document.documentElement.removeAttribute("data-sidebar-size");
    else if (layout === "semibox") {
      changeHTMLAttribute("data-layout-width", "fluid");
      changeHTMLAttribute("data-layout-style", "default");
    }
    changeHTMLAttribute("data-layout", layout);
    set({ layoutType: layout });
  },

  changeLayoutMode: (layoutMode: any) => {
    changeHTMLAttribute("data-bs-theme", layoutMode);
    const sidebarTheme = layoutMode === "dark" ? "dark" : "light";
    changeHTMLAttribute("data-sidebar", sidebarTheme);
    set({ layoutModeType: layoutMode, leftSidebarType: sidebarTheme });
  },

  changeSidebarTheme: (theme: any) => {
    changeHTMLAttribute("data-sidebar", theme);
    set({ leftSidebarType: theme });
  },

  changeLayoutWidth: (layoutWidth: any) => {
    changeHTMLAttribute("data-layout-width", layoutWidth === "lg" ? "fluid" : "boxed");
    set({ layoutWidthType: layoutWidth });
  },

  changeLayoutPosition: (layoutposition: any) => {
    changeHTMLAttribute("data-layout-position", layoutposition);
    set({ layoutPositionType: layoutposition });
  },

  changeTopbarTheme: (topbarTheme: any) => {
    changeHTMLAttribute("data-topbar", topbarTheme);
    set({ topbarThemeType: topbarTheme });
  },

  changeLeftsidebarSizeType: (leftsidebarSizetype: any) => {
    const sizeMap: Record<string, string> = {
      lg: "lg",
      md: "md",
      sm: "sm",
      "sm-hover": "sm-hover",
    };
    changeHTMLAttribute("data-sidebar-size", sizeMap[leftsidebarSizetype] || "lg");
    set({ leftsidbarSizeType: leftsidebarSizetype });
  },

  changeLeftsidebarViewType: (leftsidebarViewtype: any) => {
    changeHTMLAttribute("data-layout-style", leftsidebarViewtype);
    set({ leftSidebarViewType: leftsidebarViewtype });
  },

  changeSidebarImageType: (leftsidebarImagetype: any) => {
    changeHTMLAttribute("data-sidebar-image", leftsidebarImagetype);
    set({ leftSidebarImageType: leftsidebarImagetype });
  },

  changePreLoader: (preloaderTypes: any) => {
    changeHTMLAttribute("data-preloader", preloaderTypes);
    set({ preloader: preloaderTypes });
  },

  changeSidebarVisibility: (sidebarVisibilitytype: any) => {
    changeHTMLAttribute("data-sidebar-visibility", sidebarVisibilitytype);
    set({ sidebarVisibilitytype });
  },
});

export const useLayoutStore = create<LayoutState>()(
  persist(layoutStoreImpl, {
    name: LAYOUT_STORAGE_KEY,
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({
      layoutModeType: state.layoutModeType,
      leftSidebarType: state.leftSidebarType,
    }),
    onRehydrateStorage: () => (state, err) => {
      if (err || !state) return;
      changeHTMLAttribute("data-bs-theme", state.layoutModeType);
      changeHTMLAttribute("data-sidebar", state.leftSidebarType);
    },
  })
);
